import { useEffect } from "react";
import { BrowserRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteProvider, useSite } from "@/context/SiteContext";
import { loadThirdParty, trackPage } from "@/lib/consent";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { CookieBanner } from "@/components/site/CookieBanner";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import LegalPage from "@/pages/LegalPage";
import DynamicPage from "@/pages/DynamicPage";
import { PaymentFailed, PaymentSuccess } from "@/pages/Payment";
import NotFound from "@/pages/NotFound";
import { AdminAuthProvider, Protected } from "@/admin/AuthContext";
import AdminLayout from "@/admin/AdminLayout";
import AdminLogin from "@/admin/pages/Login";
import Dashboard from "@/admin/pages/Dashboard";
import Leads from "@/admin/pages/Leads";
import Orders from "@/admin/pages/Orders";
import { PageEditor, PagesList } from "@/admin/pages/Pages";
import { LegalEditor, LegalList } from "@/admin/pages/Legal";
import Requests from "@/admin/pages/Requests";
import Collection from "@/admin/pages/Collection";
import { ContactPricingEditor, HomepageEditor, MediaLibrary, MenusEditor } from "@/admin/pages/Content";
import { ChangePassword, GeneralSettings, Marketing } from "@/admin/pages/Settings";

const LEGAL_SLUGS = ["privacy-policy", "terms-and-conditions", "refund-policy", "shipping-policy", "disclaimer", "cookie-policy", "data-deletion", "grievance"];

const Tracker = () => {
  const { pathname } = useLocation();
  const { settings } = useSite();
  useEffect(() => {
    if (!settings) return;
    const run = () => loadThirdParty(settings);
    run();
    window.addEventListener("obt-consent", run);
    return () => window.removeEventListener("obt-consent", run);
  }, [settings]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    if (!settings) return;
    const t = setTimeout(() => trackPage(settings), 400);
    const onConsent = () => trackPage(settings);
    window.addEventListener("obt-consent", onConsent, { once: true });
    return () => { clearTimeout(t); window.removeEventListener("obt-consent", onConsent); };
  }, [pathname, settings]);
  return null;
};

const Layout = () => (
  <div className="flex min-h-screen flex-col">
    <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-cta focus:px-4 focus:py-2 focus:font-bold focus:text-navy">Skip to content</a>
    <Navbar />
    <main id="main" className="flex-1"><Outlet /></main>
    <Footer />
    <WhatsAppFloat />
    <CookieBanner />
    <Tracker />
  </div>
);

export default function App() {
  return (
    <SiteProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/login" element={<AdminAuthProvider><AdminLogin /></AdminAuthProvider>} />
          <Route path="/admin" element={<AdminAuthProvider><Protected><AdminLayout /></Protected></AdminAuthProvider>}>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="orders" element={<Orders />} />
            <Route path="pages" element={<PagesList />} />
            <Route path="pages/:id" element={<PageEditor />} />
            <Route path="legal" element={<LegalList />} />
            <Route path="legal/:id" element={<LegalEditor />} />
            <Route path="requests" element={<Requests />} />
            {["products", "services", "plans", "testimonials", "faqs"].map((r) => <Route key={r} path={r} element={<Collection key={r} resource={r} />} />)}
            <Route path="homepage" element={<HomepageEditor />} />
            <Route path="contact-page" element={<ContactPricingEditor />} />
            <Route path="menus" element={<MenusEditor />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="settings" element={<GeneralSettings />} />
            <Route path="password" element={<ChangePassword />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/page/:slug" element={<DynamicPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />
            {LEGAL_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<LegalPage key={s} slug={s} />} />)}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </SiteProvider>
  );
}
